import React from "react";
import { UseFormReturn } from "react-hook-form/dist/types/form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { ApplicationUpdateApi } from "../../../../../../../../../services/Api/Organization/types";
import { AppState } from "../../../utils/types";
import { parseStringToList } from "../../../utils/utils";

interface Props {
	appState: AppState;
	form: UseFormReturn<ApplicationUpdateApi>;
}

const MewsForm = ({ form, appState }: Props) => {
	const {
		control,
		register,
		formState: { errors },
	} = form;
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);

	const defaultCustomFields = parseStringToList(
		appState.app.settings,
		appState.config || {},
		"custom_fields_to_sync",
		"custom_fields"
	)
		?.filter((cf) => cf && (cf.label || cf.name))
		?.map((cf) => ({
			label: cf.label ? cf.label : cf.name,
			id: cf.slug,
		}));

	const defaultItemCustomFields = parseStringToList(
		appState.app.settings,
		appState.config || {},
		"item_custom_fields_to_sync",
		"item_custom_fields"
	)
		?.filter((cf) => cf && (cf.label || cf.name))
		?.map((cf) => ({
			label: cf.label ? cf.label : cf.name,
			id: cf.slug,
		}));

	const searchCustomFields = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newObj = {
			...params,
			"resource_type|e": "ORDER",
		};
		return apiOrganization.getCustomFieldsSearchSelect(search, newObj, {
			cancelToken: options?.token,
		});
	};

	const searchItemCustomFields = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newObj = {
			...params,
			"resource_type|e": "ITEM",
		};
		return apiOrganization.getCustomFieldsSearchSelect(search, newObj, {
			cancelToken: options?.token,
		});
	};

	const onSearchPaymentMethods = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getPaymentMethodsSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	return (
		<>
			<FormInput label={t("lib:common.word.name")} register={register} name="name" errors={errors} />
			<FormSelectGroup
				name="settings.payment_method_id"
				getOptionValue={(option) => option.id}
				label={t("modules.app.field.payment_method.title")}
				errors={errors}
				defaultValue={{
					label: appState?.config?.payment_method_name,
					id: appState.app.settings.payment_method_id,
				}}
				loadOptions={onSearchPaymentMethods}
				control={control}
				data-testid="settings.payment_method_id"
			/>
			<FormSelectGroup
				isMulti={true}
				name="settings.custom_fields_to_sync"
				path={`/${organization.id}/settings/payment_methods/`}
				getOptionValue={(option) => option.slug}
				label={t("modules.app.action.custom_fields_sync.title")}
				errors={errors}
				defaultValue={defaultCustomFields}
				loadOptions={searchCustomFields}
				control={control}
				data-testid="settings.custom_fields_to_sync"
			/>
			<FormSelectGroup
				isMulti={true}
				name="settings.item_custom_fields_to_sync"
				path={`/${organization.id}/settings/custom_fields/`}
				getOptionValue={(option) => option.slug}
				label={t("modules.app.action.item_custom_fields_sync.title")}
				errors={errors}
				defaultValue={defaultItemCustomFields}
				loadOptions={searchItemCustomFields}
				control={control}
				data-testid="settings.item_custom_fields_to_sync"
			/>
		</>
	);
};

export default MewsForm;
